import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form } from "react-bootstrap";
import { Card, CardBody } from "./../../components/card/card.jsx";
import Select from "react-select";
import { dateOptions } from "./../../components/selectvalues/selectvalue.jsx";
import glassshimmer from "../../assets/images/glassshimmer.png";
import DatePicker from "react-datepicker";
import Pagination from "./../../components/pagination/Pagination.jsx";
import { fetchTopRecipeEventCounts } from "../../actions/recipes_actions.js";
import "react-datepicker/dist/react-datepicker.css";
import CraftedCocktailsExpandCard from "../../components/charts/CraftedCocktailsExpandCard";

const RecipeRow = ({ recipe, rank, currentTab, themeColor, glassshimmer, tColor }) => {
  return (
    <tr style={{ verticalAlign: "middle" }}>
      <td
        style={{
          width: "40px",
          textAlign: "center",
          fontWeight: "bold",
          color: "tColor",
          borderRight: `1px solid ${tColor}`,
        }}
      >
        {rank}
      </td>
      <td style={{ width: "80px", padding: "0 10px" }}>
        <img
          src={recipe.image && recipe.image !== "none" ? recipe.image : glassshimmer}
          alt={recipe.name}
          className="img-fluid"
          style={{ width: "60px", height: "60px", objectFit: "cover", borderRadius: "0.", backgroundColor: "#fff" }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = glassshimmer;
          }}
        />
      </td>
      <td style={{ paddingLeft: "10px" }}>
        <h5 style={{ margin: "0 0 4px 0", fontSize: 15 }}>{recipe.name}</h5>
        <div>
          Created By: <span style={{ color: themeColor }}>{recipe.username}</span>
        </div>
        <div style={{ marginTop: "4px", fontSize: "13px" }} className="ingredients-fields">
          <span className="ingredients-gray">
            <strong style={{ color: themeColor }}>Base:</strong>{" "}
            {recipe.ingredientsObj?.ingredient_base?.name || "-"}
          </span>{" "}
          <span className="ingredients-gray" style={{ marginLeft: "10px" }}>
            <strong style={{ color: themeColor }}>Mixer:</strong>{" "}
            {recipe.ingredientsObj?.ingredient_mixer?.name || "-"}
          </span>{" "}
          <span className="ingredients-gray" style={{ marginLeft: "10px" }}>
            <strong style={{ color: themeColor }}>Garnish:</strong>{" "}
            {recipe.ingredientsObj?.ingredient_garnish?.name || "-"}
          </span>
        </div>
      </td>
      <td style={{ width: "100px", textAlign: "center" }}>
        <div>
          <label>
            {currentTab === "liked" || currentTab === "shared"
              ? currentTab === "liked"
                ? "Likes"
                : "Shares"
              : currentTab === "crafted"
              ? "Crafts"
              : currentTab === "clicked"
              ? "Clicks"
              : "Views"}
          </label>
          <br />
          <span style={{ color: themeColor, fontWeight: "bold" }}>{recipe.selectedCount}</span>
        </div>
      </td>
    </tr>
  );
};

const Recipe = () => {
  const dispatch = useDispatch();
  const themeColor = getComputedStyle(document.body).getPropertyValue("--bs-theme").trim();

  // Filters / paging
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [currentTab, setCurrentTab] = useState("viewed");

  // 🔑 NEW: separate search input vs. applied search term
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState(""); // only used after clicking Apply

  const [receipesselectedDateOption, setReceipesSelectedDateOption] = useState(dateOptions[2]);
  const [receipescustomDateRange, setReceipeCustomDateRange] = useState(false);
  const [receipesstartDate, setReceipesStartDate] = useState(new Date());
  const [receipesendDate, setReceipesEndDate] = useState(new Date());

  const { recipe_events_info, loading_recipe_events_info } = useSelector((state) => state.recipes);
  const recipesData = recipe_events_info?.recipes || [];
  const totalRecipes =
    recipe_events_info?.total !== undefined ? recipe_events_info.total : recipesData.length;

  useEffect(() => {
    const effectiveTotal =
      recipe_events_info?.total !== undefined ? recipe_events_info.total : recipesData.length;
    const totalPages = Math.ceil(effectiveTotal / itemsPerPage) || 1;
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [recipe_events_info, recipesData, itemsPerPage, currentPage]);

  const getCountType = (tab) => {
    switch (tab) {
      case "viewed":
        return "view";
      case "clicked":
        return "viewList";
      case "crafted":
        return "make";
      case "liked":
        return "like";
      case "shared":
        return "shared";
      default:
        return "view";
    }
  };

  const fetchData = () => {
    const orderRank = "desc";
    const datefilter = receipesselectedDateOption.value;
    const startTime = receipesstartDate.toISOString();
    const endTime = receipesendDate.toISOString();
    const countType = getCountType(currentTab);
    dispatch(
      fetchTopRecipeEventCounts(
        orderRank,
        datefilter,
        startTime,
        endTime,
        countType,
        limit,
        1,
        itemsPerPage
      )
    );
  };

  const handleDateOptionChangeRecipe = (selectedOption) => {
    setReceipesSelectedDateOption(selectedOption);
    if (selectedOption.value === 3) {
      setReceipeCustomDateRange(true);
    } else {
      setReceipeCustomDateRange(false);
      const endTimestamp = Math.floor(Date.now() / 1000);
      let startTimestamp;
      switch (selectedOption.value) {
        case 5:
          startTimestamp = endTimestamp - 24 * 60 * 60;
          break;
        case 2:
          startTimestamp = endTimestamp - 7 * 24 * 60 * 60;
          break;
        case 1: {
          const currentDate = new Date();
          startTimestamp = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getTime() / 1000;
          break;
        }
        case 0:
          startTimestamp = new Date(new Date().getFullYear(), 0, 1).getTime() / 1000;
          break;
        default:
          return;
      }
      setReceipesStartDate(new Date(startTimestamp * 1000));
      setReceipesEndDate(new Date(endTimestamp * 1000));
    }
    setCurrentPage(1);
  };

  const handleCustomDateRangeChange = (startDate, endDate) => {
    setReceipesStartDate(startDate);
    setReceipesEndDate(endDate);
    setCurrentPage(1);
  };

  const handleChangeStartRecipe = (date) => {
    setReceipesStartDate(date);
    handleCustomDateRangeChange(date, receipesendDate);
  };

  const handleChangeEndRecipe = (date) => {
    setReceipesEndDate(date);
    handleCustomDateRangeChange(receipesstartDate, date);
  };

  const handleTabChange = (tab) => {
    setCurrentTab(tab);
    setCurrentPage(1);
    setReceipeCustomDateRange(false);
    const orderRank = "desc";
    const datefilter = receipesselectedDateOption.value;
    const startTime = receipesstartDate.toISOString();
    const endTime = receipesendDate.toISOString();
    const countType = getCountType(tab);
    dispatch(
      fetchTopRecipeEventCounts(
        orderRank,
        datefilter,
        startTime,
        endTime,
        countType,
        limit,
        1,
        itemsPerPage
      )
    );
  };

  // initial load
  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 🔑 Search: only update local input while typing
  const handleSearchChange = (e) => setSearchInput(e.target.value);

  // 🔑 Apply search when clicking the button (or pressing Enter)
  const applySearch = () => {
    setSearchTerm(searchInput.trim());
    setCurrentPage(1);
    // (Optional) fetchData(); // search is client-side; no API call needed
  };

  // Client-side filtering uses the APPLIED searchTerm only
  const filteredRecipes = recipesData.filter(
    (item) =>
      item.name && item.name.toLowerCase().includes((searchTerm || "").toLowerCase())
  );

  // Pagination on the client-filtered list
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const currentRecipes = filteredRecipes.slice(indexOfFirstItem, indexOfLastItem);
  const displayStartIndex = filteredRecipes.length ? indexOfFirstItem + 1 : 0;
  const displayEndIndex = Math.min(indexOfLastItem, filteredRecipes.length);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const customPopperModifiers = {
    computeStyle: {
      enabled: true,
      fn: (data) => ({
        ...data,
        styles: { ...data.styles, zIndex: 9000 },
      }),
    },
  };

  return (
    <>
      <div style={{ paddingBottom: 10 }}>
        <h6>
          <span style={{ color: themeColor }}>PAGES</span> / RECIPES
        </h6>
        <h1 className="page-header mb-0">RECIPES PER EVENT COUNTS</h1>
      </div>

      <Card>
        <CardBody>
          <CraftedCocktailsExpandCard />
        </CardBody>
      </Card>

      <div className="col-xl-12">
        <Card className="mt-3">
          <h4 style={{ marginTop: "20px", marginLeft: "20px" }}>TOP RECIPES PER EVENT COUNTS</h4>
          <CardBody>
            <div className="col-md-12">
              {/* Filters Row */}
              <div className="row mb-3">
                <div className="col-12">
                  <div className="row g-3 align-items-end">
                    <div className="col-12 col-md-2">
                      <label htmlFor="date">Date Dimension</label>
                      <Select
                        options={dateOptions}
                        value={receipesselectedDateOption}
                        onChange={handleDateOptionChangeRecipe}
                        classNamePrefix="react-select"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 11100 }),
                          menu: (base) => ({ ...base, zIndex: 11100 }),
                        }}
                      />
                    </div>

                    <div className="col-12 col-md-2">
                      <label>Top Number</label>
                      <Form.Control
                        type="number"
                        value={limit}
                        onChange={(e) => {
                          setLimit(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        placeholder="Limit"
                        min="1"
                      />
                    </div>

                    {/* 🔑 Search input (local only) */}
                    <div className="col-12 col-md-6">
                      <label htmlFor="all-recipes-search">Search</label>
                      <Form.Control
                        id="all-recipes-search"
                        type="text"
                        value={searchInput}
                        onChange={handleSearchChange}
                        placeholder="Search"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            applySearch();
                          }
                        }}
                      />
                    </div>

                    {/* Apply button separate from input */}
                    <div className="col-auto">
                      <label className="d-none d-md-block">&nbsp;</label>
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          setCurrentPage(1);
                          applySearch();
                          fetchData(); // keeps parity with your current behavior
                        }}
                        type="button"
                      >
                        <i className="fas fa-search" />
                      </button>
                    </div>
                  </div>

                  {receipescustomDateRange && (
                    <div className="row g-3 mt-1">
                      <div className="col-6 col-md-2" style={{ position: "relative", zIndex: 11000 }}>
                        <label>Date From:</label>
                        <DatePicker
                          selected={receipesstartDate}
                          onChange={handleChangeStartRecipe}
                          className="form-control"
                          popperModifiers={customPopperModifiers}
                        />
                      </div>
                      <div className="col-6 col-md-2" style={{ position: "relative", zIndex: 11000 }}>
                        <label>Date To:</label>
                        <DatePicker
                          selected={receipesendDate}
                          onChange={handleChangeEndRecipe}
                          className="form-control"
                          popperModifiers={customPopperModifiers}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <ul className="nav nav-underline" id="myTab" role="tablist">
                {[
                  { id: "viewed", label: "Viewed" },
                  { id: "crafted", label: "Crafted" },
                  { id: "liked", label: "Liked" },
                ].map((tab, index) => (
                  <li className="nav-item" role="presentation" key={index}>
                    <button
                      className={`nav-link ${currentTab === tab.id ? "active" : ""}`}
                      id={`${tab.id}-tab`}
                      data-bs-toggle="tab"
                      data-bs-target={`#${tab.id}`}
                      type="button"
                      role="tab"
                      aria-controls={tab.id}
                      aria-selected={currentTab === tab.id ? "true" : "false"}
                      onClick={() => handleTabChange(tab.id)}
                    >
                      {tab.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Summary Info */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: "10px",
                fontSize: "14px",
                color: "#fff",
              }}
            >
              {loading_recipe_events_info ? (
                <small>Loading recipes...</small>
              ) : (
                <small>
                  <span className="text-inverse">Showing </span>
                  <span style={{ color: themeColor }}>{displayStartIndex}</span>
                  <span className="text-inverse"> - </span>
                  <span style={{ color: themeColor }}>{displayEndIndex}</span>
                  <span className="text-inverse"> of </span>
                  <span style={{ color: themeColor }}>{filteredRecipes.length}</span>
                  <span className="text-inverse"> recipes</span>
                </small>
              )}
            </div>

            <div
              className="table-responsive"
              style={{
                minHeight: "300px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "visible",
              }}
            >
              {loading_recipe_events_info ? (
                <div className="spinner-border" style={{ color: themeColor }} role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : currentRecipes.length === 0 ? (
                <div>No Records Found</div>
              ) : (
                <table className="table table-bordered">
                  <tbody>
                    {currentRecipes.map((item, index) => (
                      <RecipeRow
                        key={item.id || index}
                        recipe={item}
                        rank={indexOfFirstItem + index + 1}
                        currentTab={currentTab}
                        themeColor={themeColor}
                        glassshimmer={glassshimmer}
                        tColor={themeColor}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </CardBody>
        </Card>

        <div className="row mt-3">
          <div className="col-md-10"></div>
          <div className="col-md-2">
            <Pagination
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalItems={filteredRecipes.length}
              paginate={paginate}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Recipe;
